// ================================
// TELEGRAM CONNECT (shared by driver + customer)
// One hook backing both the dismissible "Connect Telegram" card and the
// header button that reopens it — or, once linked, jumps straight to the
// user's actual Telegram chat with the bot instead of the connect flow.
// ================================
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

export default function useTelegramConnect(scope) {
  const dismissKey = `ww_telegram_dismissed_${scope}`;

  const [linked, setLinked] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [code, setCode] = useState(null);
  const [botUsername, setBotUsername] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(dismissKey) === '1'; } catch { return false; }
  });

  const checkStatus = useCallback(async () => {
    try {
      const res = await axios.get('/api/telegram/status');
      setLinked(res.data.linked);
      if (res.data.botUsername) setBotUsername(res.data.botUsername);
    } catch (e) {}
  }, []);

  useEffect(() => { checkStatus(); }, [checkStatus]);

  function dismiss() {
    setDismissed(true);
    try { localStorage.setItem(dismissKey, '1'); } catch {}
  }

  function reopen() {
    setDismissed(false);
    try { localStorage.removeItem(dismissKey); } catch {}
  }

  async function connect() {
    setConnecting(true);
    try {
      const res = await axios.post('/api/telegram/link-code');
      if (res.data.botUsername) setBotUsername(res.data.botUsername);
      if (res.data.deepLink) window.open(res.data.deepLink, '_blank');
      else setCode(res.data.code);

      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        const res2 = await axios.get('/api/telegram/status').catch(() => null);
        if (res2?.data?.linked) {
          setLinked(true);
          setConnecting(false);
          setCode(null);
          clearInterval(poll);
        } else if (attempts >= 15) {
          setConnecting(false);
          clearInterval(poll);
        }
      }, 3000);
    } catch (e) {
      setConnecting(false);
    }
  }

  // The header button's click handler: already linked -> go straight to the
  // Telegram chat itself; not linked -> bring the connect card back if it's
  // been dismissed (or just leave it visible if it's already showing).
  function openOrReopen() {
    if (linked && botUsername) {
      window.open(`https://t.me/${botUsername}`, '_blank');
    } else {
      reopen();
    }
  }

  return { linked, connecting, code, dismissed, connect, dismiss, openOrReopen };
}
